import { Component, DestroyRef, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { ButtonModule } from '@openng/optimus-ui/button';
import { CheckboxModule } from '@openng/optimus-ui/checkbox';
import { SkeletonModule } from '@openng/optimus-ui/skeleton';
import { MessageService } from '@openng/optimus-ui/api';
import { rxResourceFixed } from '@zayan-erp/shared';
import { DsmDealOption } from '../../../models/dsm-deal-options-view.models';
import { DsmRoomReplacementRerun } from '../../../models/dsm-room-replacement-review.models';
import { DsmDealOptionsService } from '../../../services/dsm-deal-options.service';

interface RerunRuleRow {
  ruleId: string;
  trigger: string;
  replacedRooms: string;
  newRooms: string;
}

interface RerunCancelledLineRow {
  addendumProductId: string;
  addendumLabel: string;
  productCode: string;
  locationName: string;
}

/**
 * Previews, then runs, the room replacement rules of an addendum that is already locked — for
 * addendums that locked before the rule existed. The preview is the server's real run minus the
 * save, so what it lists is exactly what "Run rules" does.
 */
@Component({
  selector: 'dsm-room-replacement-rerun',
  standalone: true,
  imports: [ButtonModule, CheckboxModule, SkeletonModule, FormsModule],
  templateUrl: './dsm-room-replacement-rerun.component.html'
})
export class DsmRoomReplacementRerunComponent {
  readonly #dealOptionsService = inject(DsmDealOptionsService);
  readonly #messageService = inject(MessageService);
  readonly #destroyRef = inject(DestroyRef);

  readonly addendum = input.required<DsmDealOption>();

  readonly rerun = output<DsmRoomReplacementRerun>();
  readonly dialogClose = output<void>();

  readonly notifyConsultants = signal(true);
  readonly isRunning = signal(false);

  readonly previewResource = rxResourceFixed({
    params: () => ({ addendumId: this.addendum().id }),
    defaultValue: null as DsmRoomReplacementRerun | null,
    stream: ({ params }) => this.#dealOptionsService.previewRoomReplacementRerun(params.addendumId)
  });

  readonly isLoading = computed(() => this.previewResource.isLoading());
  // `error()` returns `Error | undefined`, so use `!= null` to catch both null and undefined.
  readonly hasError = computed(() => this.previewResource.error() != null);
  readonly addendumLabel = computed(() => `Addendum #${this.addendum().seq}`);

  readonly ruleRows = computed<RerunRuleRow[]>(() =>
    (this.previewResource.value()?.rules ?? []).map(rule => ({
      ruleId: rule.ruleId,
      trigger: [rule.triggerProductCode, rule.triggerProductDescription].filter(Boolean).join(' · ') || 'Plan change',
      replacedRooms: rule.replacedRoomNames.join(', '),
      newRooms: rule.newRoomNames.join(', ')
    }))
  );

  readonly cancelledLineRows = computed<RerunCancelledLineRow[]>(() =>
    (this.previewResource.value()?.cancelledLines ?? []).map(line => ({
      addendumProductId: line.addendumProductId,
      addendumLabel: line.addendumSequenceNumber != null ? `Addendum #${line.addendumSequenceNumber}` : 'Addendum',
      productCode: line.productCode,
      locationName: line.locationName
    }))
  );

  readonly hasRulesToRun = computed(() => this.ruleRows().length > 0);
  readonly cancelledCount = computed(() => this.cancelledLineRows().length);
  readonly cancelledCountLabel = computed(
    () => `${this.cancelledCount()} option${this.cancelledCount() === 1 ? '' : 's'}`
  );
  // Cancelled signed options are exactly what a consultant has to hear about.
  readonly isCancellingSilently = computed(() => this.cancelledCount() > 0 && !this.notifyConsultants());
  readonly isRunDisabled = computed(
    () => this.isLoading() || this.hasError() || !this.hasRulesToRun() || this.isRunning()
  );
  readonly runSeverity = computed<'danger' | 'primary'>(() => (this.cancelledCount() > 0 ? 'danger' : 'primary'));

  retry(): void {
    this.previewResource.reload();
  }

  run(): void {
    if (this.isRunDisabled()) return;

    this.isRunning.set(true);
    this.#dealOptionsService
      .rerunRoomReplacementRules(this.addendum().id, this.notifyConsultants())
      .pipe(
        finalize(() => this.isRunning.set(false)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe({
        next: result => {
          this.#toastResult(result);
          this.rerun.emit(result);
        }
      });
  }

  close(): void {
    this.dialogClose.emit();
  }

  #toastResult(result: DsmRoomReplacementRerun): void {
    // Not applied means another run executed these rules between the preview and this one.
    if (!result.applied) {
      this.#messageService.add({
        severity: 'info',
        summary: `Location rules had already run on home, so nothing changed.`
      });
      return;
    }

    const cancelled = result.cancelledLines.length;
    const cancelledSummary = cancelled > 0 ? ` ${cancelled} option${cancelled === 1 ? '' : 's'} cancelled.` : '';
    this.#messageService.add({
      severity: 'success',
      summary: `Location rules applied to ${this.addendumLabel()}.${cancelledSummary}`
    });
  }
}
